// UsersList.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FaKey,
  FaCheck,
  FaUserMinus,
  FaBuilding,
  FaTimes,
  FaUserPlus,
  FaSitemap,
  FaClock,
  FaSync
} from 'react-icons/fa';
import { Users, Building, Shield } from 'lucide-react';
import { useSocket } from '../../SocketContext';
import styles from '../CSS/UsersList.module.css';

const UsersList = ({ 
  users = [], 
  stats, 
  selectedOU, 
  isMobile, 
  serverUrl, 
  showNotification, 
  onDataChange,
  isOUView = false 
}) => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendAction, setSuspendAction] = useState('suspend'); // 'suspend' or 'activate'
  const [singleUserPassword, setSingleUserPassword] = useState(null);
  const [singleUserSuspend, setSingleUserSuspend] = useState(null); // Para guardar el usuario individual a suspender
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loadingUsers, setLoadingUsers] = useState(new Set()); // Estado de carga por usuario
  const [isModalLoading, setIsModalLoading] = useState(false); // Estado de carga para modales
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    forceChange: true
  });

  // Socket.IO para actualizaciones en tiempo real
  const { socket } = useSocket();

  const reloadData = useCallback(async () => {
    if (onDataChange) {
      // NO usar setIsLoading aquí - el padre maneja la recarga silenciosa
      try {
        await onDataChange();
      } catch (error) {
        console.error('Error recargando datos:', error);
      }
    }
  }, [onDataChange]);

  // useEffect para escuchar eventos de Socket.IO
  useEffect(() => {
    if (!socket) return;

    // Escuchar evento de usuario actualizado
    socket.on('workspace:user-updated', (data) => {
      console.log('Usuario actualizado en tiempo real:', data);
      if (showNotification) {
        showNotification(`Usuario ${data.user?.primaryEmail || data.userId} actualizado`, 'info');
      }
      reloadData();
    });

    // Escuchar evento de usuario creado
    socket.on('workspace:user-created', (data) => {
      console.log('Usuario creado en tiempo real:', data);
      if (showNotification) {
        showNotification(`Nuevo usuario: ${data.user.primaryEmail}`, 'success');
      }
      reloadData();
    });

    // Escuchar evento de usuario eliminado
    socket.on('workspace:user-deleted', (data) => {
      console.log('Usuario eliminado en tiempo real:', data);
      if (showNotification) {
        showNotification(`Usuario eliminado`, 'info');
      }
      reloadData();
    });

    // Escuchar evento de usuario suspendido/activado
    socket.on('workspace:user-suspended', (data) => {
      console.log('Usuario suspendido/activado en tiempo real:', data);
      if (showNotification) {
        showNotification(`Usuario ${data.suspended ? 'suspendido' : 'activado'}`, 'info');
      }
      reloadData();
    });

    // Escuchar evento de cambio de contraseña
    socket.on('workspace:password-changed', (data) => {
      console.log('Contraseña cambiada en tiempo real:', data);
      if (showNotification) {
        showNotification(`Contraseña actualizada`, 'info');
      }
    });

    // Escuchar evento de cambio masivo de contraseña
    socket.on('workspace:bulk-password-changed', (data) => {
      console.log('Contraseñas cambiadas en masa en tiempo real:', data);
      if (showNotification && data.result) {
        showNotification(`${data.result.updated || data.userIds.length} contraseñas actualizadas`, 'info');
      }
    });

    // Escuchar evento de datos actualizados
    socket.on('workspace:data-refreshed', (data) => {
      console.log('Datos de Workspace actualizados:', data);
      if (showNotification) {
        showNotification('Datos actualizados', 'info');
      }
      reloadData();
    });

    // Cleanup: remover listeners cuando el componente se desmonte
    return () => {
      socket.off('workspace:user-updated');
      socket.off('workspace:user-created');
      socket.off('workspace:user-deleted');
      socket.off('workspace:user-suspended');
      socket.off('workspace:password-changed');
      socket.off('workspace:bulk-password-changed');
      socket.off('workspace:data-refreshed');
    };
  }, [socket, showNotification, reloadData]);

  const toggleUserSelection = useCallback((userId, e) => {
    e?.stopPropagation();
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      newSet.has(userId) ? newSet.delete(userId) : newSet.add(userId);
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedUsers(prev => 
      prev.size === users.length ? new Set() : new Set(users.map(u => u.primaryEmail))
    );
  }, [users]);

  const clearSelection = useCallback(() => setSelectedUsers(new Set()), []);

  const changePassword = useCallback(async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification('Las contraseñas no coinciden', 'error');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      showNotification('La contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }

    const userIds = singleUserPassword ? [singleUserPassword] : Array.from(selectedUsers);
    if (userIds.length === 0) return;

    // Activar loading del modal
    setIsModalLoading(true);

    let successCount = 0;
    let errorCount = 0;

    for (const userId of userIds) {
      try {
        const response = await fetch(`${serverUrl}/api/workspace/user/${encodeURIComponent(userId)}/password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            newPassword: passwordData.newPassword,
            forceChangePassword: passwordData.forceChange
          })
        });
        const result = await response.json();
        result.success ? successCount++ : errorCount++;
      } catch {
        errorCount++;
      }
    }

    // Desactivar loading del modal
    setIsModalLoading(false);

    if (successCount > 0) {
      showNotification(`Contraseña cambiada para ${successCount} usuario${successCount > 1 ? 's' : ''}`, 'success');
    }
    if (errorCount > 0) {
      showNotification(`Error en ${errorCount} usuario${errorCount > 1 ? 's' : ''}`, 'error');
    }

    // Limpiar estados primero, antes de cerrar el modal
    setSingleUserPassword(null);
    setSelectedUsers(new Set());

    // Cerrar modal y resetear password data
    setShowPasswordModal(false);
    setPasswordData({ newPassword: '', confirmPassword: '', forceChange: true });

    // Recargar datos si hubo éxito
    if (successCount > 0) {
      await reloadData();
    }
  }, [passwordData, selectedUsers, singleUserPassword, serverUrl, showNotification, reloadData]);

  const toggleUserSuspension = useCallback(async (userId, currentlySuspended) => {
    // Agregar usuario al estado de carga
    setLoadingUsers(prev => new Set(prev).add(userId));

    try {
      const response = await fetch(`${serverUrl}/api/workspace/user/${encodeURIComponent(userId)}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suspended: !currentlySuspended })
      });
      const result = await response.json();
      if (result.success) {
        showNotification(`Usuario ${!currentlySuspended ? 'suspendido' : 'reactivado'}`, 'success');
        await reloadData();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      showNotification('Error: ' + error.message, 'error');
    } finally {
      // Remover usuario del estado de carga
      setLoadingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  }, [serverUrl, showNotification, reloadData]);

  const suspendMultipleUsers = useCallback(async () => {
    // Determinar si es un solo usuario o múltiples
    const userIds = singleUserSuspend ? [singleUserSuspend.email] : Array.from(selectedUsers);
    if (userIds.length === 0) return;

    // Activar loading del modal
    setIsModalLoading(true);

    let successCount = 0;
    let errorCount = 0;

    for (const userId of userIds) {
      try {
        const response = await fetch(`${serverUrl}/api/workspace/user/${encodeURIComponent(userId)}/suspend`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suspended: suspendAction === 'suspend' })
        });
        const result = await response.json();
        result.success ? successCount++ : errorCount++;
      } catch {
        errorCount++;
      }
    }

    // Desactivar loading del modal
    setIsModalLoading(false);

    // Notificaciones
    if (successCount > 0) {
      const action = suspendAction === 'suspend' ? 'suspendidos' : 'reactivados';
      showNotification(`${successCount} usuario${successCount > 1 ? 's' : ''} ${action}`, 'success');
    }
    if (errorCount > 0) {
      showNotification(`Error en ${errorCount} usuario${errorCount > 1 ? 's' : ''}`, 'error');
    }

    // Limpiar estados primero, antes de cerrar el modal
    setSingleUserSuspend(null);
    setSelectedUsers(new Set());

    // Cerrar modal
    setShowSuspendModal(false);

    // Recargar datos si hubo éxito
    if (successCount > 0) {
      await reloadData();
    }
  }, [selectedUsers, singleUserSuspend, suspendAction, serverUrl, showNotification, reloadData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedCount = useMemo(() => selectedUsers.size, [selectedUsers]);
  const allSelected = useMemo(() => users.length > 0 && selectedUsers.size === users.length, [users.length, selectedUsers.size]);

  return (
    <div className={styles.panel}>
      {/* Stats */}
      {!isOUView && stats && (
        <div className={styles.stats}>
          <div className={styles.statsGrid}>
            <div className={styles.stat}>
              <Users size={18} />
              <div>
                <span className={styles.statNum}>{stats.totalUsers}</span>
                <span className={styles.statLabel}>Total</span>
              </div>
            </div>
            <div className={styles.stat}>
              <FaCheck />
              <div>
                <span className={styles.statNum}>{stats.activeUsers}</span>
                <span className={styles.statLabel}>Activos</span>
              </div>
            </div>
            <div className={styles.stat}>
              <FaUserMinus />
              <div>
                <span className={styles.statNum}>{stats.suspendedUsers}</span>
                <span className={styles.statLabel}>Suspendidos</span>
              </div>
            </div>
            <div className={styles.stat}>
              <Shield size={14} />
              <div>
                <span className={styles.statNum}>{stats.adminUsers}</span>
                <span className={styles.statLabel}>Admins</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OU Header con selección */}
      {isOUView && selectedOU && (
        <div className={styles.ouHeader}>
          <div className={styles.ouInfo}>
            <FaBuilding />
            <div>
              <h3>{selectedOU}</h3>
              <span>{users.length} usuarios</span>
            </div>
          </div>
          {users.length > 0 && (
            <div className={styles.ouActions}>
              <button onClick={toggleSelectAll} className={styles.selectBtn} title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}>
                <FaCheck />
                <span>{allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}</span>
              </button>
              {selectedCount > 0 && (
                <>
                  <span className={styles.selectedCount}>
                    <Users size={14} />
                    {selectedCount}
                  </span>
                  <button onClick={() => setShowPasswordModal(true)} className={styles.passwordBtn} title="Cambiar contraseña">
                    <FaKey />
                    <span>Contraseña</span>
                  </button>
                  <button
                    onClick={() => {
                      setSuspendAction('suspend');
                      setShowSuspendModal(true);
                    }}
                    className={styles.suspendBtn}
                    title="Suspender usuarios"
                  >
                    <FaUserMinus />
                    <span>Suspender</span>
                  </button>
                  <button
                    onClick={() => {
                      setSuspendAction('activate');
                      setShowSuspendModal(true);
                    }}
                    className={styles.activateBtn}
                    title="Reactivar usuarios"
                  >
                    <FaUserPlus />
                    <span>Reactivar</span>
                  </button>
                  <button onClick={clearSelection} className={styles.clearBtn} title="Limpiar selección">
                    <FaTimes />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* No selection */}
      {isOUView && !selectedOU && (
        <div className={styles.empty}>
          <FaSitemap size={40} />
          <h4>Selecciona una unidad organizativa</h4>
          <p>Elige una OU del panel izquierdo</p>
        </div>
      )}

      {/* Users List */}
      {((!isOUView) || (isOUView && selectedOU)) && (
        <div className={styles.list}>
          {users.length > 0 ? (
            users.map(user => {
              const isSelected = selectedUsers.has(user.primaryEmail);
              const userName = `${user.name?.givenName || ''} ${user.name?.familyName || ''}`.trim() || 'Sin nombre';

              const isUserLoading = loadingUsers.has(user.primaryEmail);

              return (
                <div
                  key={user.primaryEmail}
                  className={`${styles.card} ${isSelected ? styles.selected : ''} ${isUserLoading ? styles.loading : ''}`}
                  onClick={(e) => !isUserLoading && toggleUserSelection(user.primaryEmail, e)}
                >
                  <div className={styles.cardMain}>
                    <div className={styles.info}>
                      <h4>{userName}</h4>
                      <p>{user.primaryEmail}</p>
                      <div className={styles.meta}>
                        <span className={user.suspended ? styles.suspended : styles.active}>
                          {user.suspended ? 'Suspendido' : 'Activo'}
                        </span>
                        {(user.isAdmin || user.isDelegatedAdmin) && (
                          <span className={styles.admin}>
                            <Shield size={10} /> Admin
                          </span>
                        )}

                        {!isMobile && user.lastLoginTime && (
                          <span className={styles.login}>
                            <FaClock size={10} /> {formatDate(user.lastLoginTime)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.actions}>
                      {isUserLoading ? (
                        <div className={styles.cardLoader}>
                          <FaSync className={styles.spin} />
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Limpiar selección múltiple y actualizar estados
                              setSelectedUsers(new Set());
                              setSingleUserPassword(user.primaryEmail);
                              setShowPasswordModal(true);
                            }}
                            className={`${styles.action} ${styles.password}`}
                            title="Cambiar contraseña"
                            disabled={isUserLoading}
                          >
                            <FaKey />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Limpiar selección múltiple y actualizar estados
                              setSelectedUsers(new Set());
                              setSingleUserSuspend({ email: user.primaryEmail, suspended: user.suspended });
                              setSuspendAction(user.suspended ? 'activate' : 'suspend');
                              setShowSuspendModal(true);
                            }}
                            className={`${styles.action} ${user.suspended ? styles.activate : styles.suspend}`}
                            title={user.suspended ? 'Reactivar' : 'Suspender'}
                            disabled={isUserLoading}
                          >
                            {user.suspended ? <FaUserPlus /> : <FaUserMinus />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.empty}>
              <Users size={40} />
              <h4>No hay usuarios</h4>
            </div>
          )}
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className={styles.overlay} onClick={(e) => {
          if (!isModalLoading && e.target === e.currentTarget) {
            setShowSuspendModal(false);
            setSingleUserSuspend(null);
          }
        }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>
                {suspendAction === 'suspend' ? <FaUserMinus /> : <FaUserPlus />}
                {' '}
                {suspendAction === 'suspend' ? 'Suspender Usuarios' : 'Reactivar Usuarios'}
              </h3>
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSingleUserSuspend(null);
                }}
                className={styles.close}
                disabled={isModalLoading}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.modalInfo}>
                {(() => {
                  const count = singleUserSuspend ? 1 : selectedCount;
                  return `¿Estás seguro de que quieres ${suspendAction === 'suspend' ? 'suspender' : 'reactivar'} ${count} usuario${count > 1 ? 's' : ''}?`;
                })()}
              </p>
            </div>

            <div className={styles.modalFooter}>
              <button
                onClick={suspendMultipleUsers}
                className={styles.save}
                disabled={isModalLoading}
              >
                {isModalLoading ? (
                  <FaSync className={styles.spin} />
                ) : (
                  <>
                    <FaCheck /> Confirmar
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSingleUserSuspend(null);
                }}
                className={styles.cancel}
                disabled={isModalLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className={styles.overlay} onClick={(e) => {
          if (!isModalLoading && e.target === e.currentTarget) {
            setShowPasswordModal(false);
            setSingleUserPassword(null);
            setPasswordData({ newPassword: '', confirmPassword: '', forceChange: true });
          }
        }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3><FaKey /> Cambiar Contraseña</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSingleUserPassword(null);
                  setPasswordData({ newPassword: '', confirmPassword: '', forceChange: true });
                }}
                className={styles.close}
                disabled={isModalLoading}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.modalInfo}>
                {(() => {
                  const count = singleUserPassword ? 1 : selectedCount;
                  return `Cambiar contraseña para ${count} usuario${count > 1 ? 's' : ''}`;
                })()}
              </p>

              <div className={styles.field}>
                <label>Nueva Contraseña</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  placeholder="Mínimo 8 caracteres"
                  disabled={isModalLoading}
                />
              </div>

              <div className={styles.field}>
                <label>Confirmar Contraseña</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  disabled={isModalLoading}
                />
              </div>

              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={passwordData.forceChange}
                  onChange={(e) => setPasswordData({...passwordData, forceChange: e.target.checked})}
                  disabled={isModalLoading}
                />
                Forzar cambio en el próximo login
              </label>
            </div>

            <div className={styles.modalFooter}>
              <button
                onClick={changePassword}
                className={styles.save}
                disabled={isModalLoading}
              >
                {isModalLoading ? (
                  <FaSync className={styles.spin} />
                ) : (
                  <>
                    <FaCheck /> Cambiar
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSingleUserPassword(null);
                  setPasswordData({ newPassword: '', confirmPassword: '', forceChange: true });
                }}
                className={styles.cancel}
                disabled={isModalLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;